import {
  DatePicker,
  Field,
  Input,
  InputNumber,
  RadioGroup,
  Spin,
} from '@/components/commons';
import { UploadService } from '@/services/upload';
import {
  FALLBACK_URL,
  FILE_SIZE_LIMIT,
  ImageService,
  SUPPORTED_EXTENSION,
  SUPPORTED_FORMATS,
} from '@/services/image';
import {
  ScreenMediaStatus,
  ScreenMediaType,
} from '@/types/response/screenMedia';
import { notificationMessage, NOTIFY_STATUS } from '@/utils';
import {
  SCREEN_MEDIA_VIDEO_PROFILE,
  validateVideoFile,
  VIDEO_FILE_SIZE_LIMIT,
} from '@/utils/videoValidation';
import { CloseOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Image, Upload } from 'antd';
import moment, { Moment } from 'moment';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

interface IProps {
  isLoading: boolean;
}

const mediaTypeOptions = [
  { label: '動画', value: ScreenMediaType.VIDEO },
  { label: '静止画', value: ScreenMediaType.IMAGE },
];

const statusOptions = [
  { label: '下書き', value: ScreenMediaStatus.DRAFT },
  { label: '公開', value: ScreenMediaStatus.PUBLIC },
  { label: '非表示', value: ScreenMediaStatus.HIDDEN },
];

/** 公開開始日時に過去は選べるが、分より細かい入力はさせない */
const disabledSecond = () => [];

export const ScreenMediaForm: React.FC<IProps> = ({ isLoading }) => {
  const { watch, setValue, setError, clearErrors, trigger } = useFormContext();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [videoWarnings, setVideoWarnings] = useState<string[]>([]);

  const mediaType = watch('mediaType');
  const mediaUrl = watch('mediaUrl');
  const posterUrl = watch('posterUrl');
  const durationSec = watch('durationSec');
  const width = watch('width');
  const height = watch('height');
  const fileSizeBytes = watch('fileSizeBytes');
  const isVideo = mediaType === ScreenMediaType.VIDEO;

  const clearMedia = () => {
    setValue('mediaUrl', '', { shouldValidate: true });
    setValue('posterUrl', null);
    setValue('durationSec', null);
    setValue('width', null);
    setValue('height', null);
    setValue('fileSizeBytes', null);
    setValue('videoCodec', null);
    setVideoWarnings([]);
  };

  /**
   * 動画の入稿。ブラウザで先に弾けるものは弾いてからアップロードする
   * （100MB 送ってからエラーになるのを避ける）。最終的な可否はサーバーが決める。
   */
  const handleVideoUpload = async (file: File) => {
    setIsUploading(true);
    setVideoWarnings([]);

    try {
      const validation = await validateVideoFile(
        file,
        SCREEN_MEDIA_VIDEO_PROFILE,
      );

      if (validation.errors.length > 0) {
        setError('mediaUrl', {
          type: 'validate',
          message: validation.errors.join('\n'),
        });

        return;
      }

      clearErrors('mediaUrl');
      setVideoWarnings(validation.warnings);

      const res = await UploadService.uploadVideo(file);

      setValue('mediaUrl', res?.data?.url ?? '', { shouldValidate: true });
      setValue('durationSec', res?.data?.durationSec ?? null);
      setValue('width', res?.data?.width ?? null);
      setValue('height', res?.data?.height ?? null);
      setValue('videoCodec', res?.data?.videoCodec ?? null);
      setValue('fileSizeBytes', res?.data?.fileSizeBytes ?? null);

      // 1 フレーム目から作ったポスターをそのまま使う。
      // 運用者に別途用意させると忘れられるうえ、再生開始の瞬間に絵が飛ぶ。
      if (validation.posterFile) {
        const poster = await ImageService.uploadFile(validation.posterFile);
        setValue('posterUrl', poster?.data?.url ?? null, {
          shouldValidate: true,
        });
      }
    } catch (error) {
      const message =
        (error as any)?.message ??
        '動画のアップロードに失敗しました。もう一度お試しください。';
      setError('mediaUrl', { type: 'validate', message });
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageUpload = async (file: File) => {
    try {
      setIsUploading(true);
      const res = await ImageService.uploadFile(file);
      setValue('mediaUrl', res?.data?.url ?? '', { shouldValidate: true });
    } catch (error) {
      notificationMessage({
        message: '画像のアップロードに失敗しました。',
        type: NOTIFY_STATUS.ERROR,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handlePosterUpload = async (file: File) => {
    try {
      setIsUploading(true);
      const res = await ImageService.uploadFile(file);
      setValue('posterUrl', res?.data?.url ?? null, { shouldValidate: true });
    } catch (error) {
      notificationMessage({
        message: 'ポスター画像のアップロードに失敗しました。',
        type: NOTIFY_STATUS.ERROR,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const beforeUploadImage =
    (field: 'mediaUrl' | 'posterUrl') => (file: File) => {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();

      if (file.size > FILE_SIZE_LIMIT) {
        setError(field, {
          type: 'validate',
          message: '画像のサイズは10MBを超えています。',
        });

        return Upload.LIST_IGNORE;
      }

      if (
        !SUPPORTED_EXTENSION.includes(fileExtension || '') ||
        !SUPPORTED_FORMATS.includes(file.type)
      ) {
        setError(field, {
          type: 'validate',
          message: '画像は .jpg、.png、.jpeg 形式ではありません。',
        });

        return Upload.LIST_IGNORE;
      }

      clearErrors(field);
      if (field === 'mediaUrl') handleImageUpload(file);
      else handlePosterUpload(file);

      return true;
    };

  const beforeUploadVideo = (file: File) => {
    if (file.size > VIDEO_FILE_SIZE_LIMIT) {
      setError('mediaUrl', {
        type: 'validate',
        message: '動画のサイズは100MBを超えています。',
      });

      return Upload.LIST_IGNORE;
    }

    handleVideoUpload(file);

    return true;
  };

  return (
    <Form layout='vertical'>
      <Spin spinning={isLoading || isUploading}>
        <Field label='管理名' name='title' required>
          <Input placeholder='例: 2026年10月 ブランド動画' maxLength={100} />
        </Field>

        <Field label='種別' name='mediaType' className='w-full'>
          {/*
            Field は {...field, ...children.props} の順でマージするため、
            onChange を渡すと react-hook-form の onChange を上書きしてしまう。
            値の更新は setValue で自分で行う（Banner の
            handleSourceTypeChange と同じ扱い）。
          */}
          <RadioGroup
            options={mediaTypeOptions}
            onChange={(e) => {
              setValue('mediaType', e.target.value, { shouldValidate: true });
              clearMedia();
            }}
          />
        </Field>

        <label className='title-calendar font-semibold'>
          {isVideo ? '動画' : '静止画'}
          <span className='text-red-500'> *</span>
        </label>
        <div className='pt-2 mb-4'>
          {!mediaUrl ? (
            <div className='border-solid rounded border-2 border-zinc-300 w-[240px] h-[100px] flex justify-center items-center'>
              <Upload
                beforeUpload={
                  isVideo ? beforeUploadVideo : beforeUploadImage('mediaUrl')
                }
                customRequest={() => {}}
                accept={isVideo ? 'video/mp4' : 'image/jpeg,image/png'}
                listType='text'
                maxCount={1}
                showUploadList={false}
              >
                <Button
                  type='default'
                  loading={isUploading}
                  className='text-[#84ab42] border border-[#84ab42] font-semibold'
                >
                  {isVideo ? '動画を選択' : '画像を選択'}
                </Button>
              </Upload>
            </div>
          ) : (
            <div className='relative inline-block'>
              {isVideo ? (
                <video
                  src={mediaUrl}
                  poster={posterUrl ?? undefined}
                  controls
                  muted
                  loop
                  playsInline
                  className='block rounded-lg bg-black'
                  style={{ width: 216, aspectRatio: '4 / 5' }}
                />
              ) : (
                <Image
                  width={216}
                  src={mediaUrl}
                  className='block rounded-lg'
                  fallback={FALLBACK_URL}
                />
              )}
              <Button
                type='primary'
                shape='circle'
                icon={<CloseOutlined />}
                size='small'
                onClick={clearMedia}
                className='absolute -top-2 -right-2 z-10 bg-red-500 border-none'
              />
            </div>
          )}
        </div>
        <Field name='mediaUrl' className='mt-[-20px]'>
          <></>
        </Field>

        {isVideo && (
          <div className='mb-4 text-sm text-gray-500'>
            推奨: {SCREEN_MEDIA_VIDEO_PROFILE.recommendedResolution.width}×
            {SCREEN_MEDIA_VIDEO_PROFILE.recommendedResolution.height}（
            {SCREEN_MEDIA_VIDEO_PROFILE.aspectRatioLabel}）/ H.264 /
            {SCREEN_MEDIA_VIDEO_PROFILE.recommendedMaxDurationSec}秒以内 /
            faststart 有効
            {width && height ? (
              <div className='mt-1 text-black'>
                この動画: {width}×{height}
                {durationSec ? ` / ${Math.round(Number(durationSec))}秒` : ''}
                {fileSizeBytes
                  ? ` / ${(Number(fileSizeBytes) / 1024 / 1024).toFixed(1)}MB`
                  : ''}
              </div>
            ) : null}
          </div>
        )}

        {videoWarnings.length > 0 && (
          <Alert
            type='warning'
            showIcon
            className='mb-4'
            message='確認してください'
            description={
              <ul className='m-0 pl-4'>
                {videoWarnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            }
          />
        )}

        {isVideo && (
          <>
            <label className='title-calendar font-semibold'>
              ポスター画像（再生開始までの表示）
            </label>
            <div className='pt-2 mb-4'>
              {!posterUrl ? (
                <Upload
                  beforeUpload={beforeUploadImage('posterUrl')}
                  customRequest={() => {}}
                  accept='image/jpeg,image/png'
                  listType='text'
                  maxCount={1}
                  showUploadList={false}
                >
                  <Button type='default' loading={isUploading}>
                    画像を選択
                  </Button>
                </Upload>
              ) : (
                <div className='relative inline-block'>
                  <Image
                    width={120}
                    src={posterUrl}
                    className='block rounded-lg'
                    fallback={FALLBACK_URL}
                  />
                  <Button
                    type='primary'
                    shape='circle'
                    icon={<CloseOutlined />}
                    size='small'
                    onClick={() =>
                      setValue('posterUrl', null, { shouldValidate: true })
                    }
                    className='absolute -top-2 -right-2 z-10 bg-red-500 border-none'
                  />
                </div>
              )}
            </div>
            <Field name='posterUrl' className='mt-[-20px]'>
              <></>
            </Field>
          </>
        )}

        <label className='title-calendar font-semibold'>公開開始日時</label>
        <div className='flex items-baseline gap-1 pt-2'>
          <Field name='publicAt' className='flex-1'>
            <DatePicker
              showTime
              disabledTime={() => ({ disabledSeconds: disabledSecond })}
              onCalendarChange={(e) => {
                setValue('publicAt', e ? (e as Moment).second(0) : undefined);
                trigger('publicAt');
                trigger('publicEndAt');
              }}
            />
          </Field>
        </div>

        <label className='title-calendar font-semibold'>公開終了日時</label>
        <div className='flex items-baseline gap-1 pt-2'>
          <Field name='publicEndAt' className='flex-1'>
            <DatePicker
              showTime
              disabledDate={(current) => {
                const publicAt = watch('publicAt');

                return (
                  !!publicAt &&
                  !!current &&
                  current < moment(publicAt).startOf('day')
                );
              }}
              disabledTime={() => ({ disabledSeconds: disabledSecond })}
              onCalendarChange={(e) => {
                setValue(
                  'publicEndAt',
                  e ? (e as Moment).second(0) : undefined,
                );
                trigger('publicEndAt');
              }}
            />
          </Field>
        </div>
        <div className='mt-[-8px] mb-4 text-sm text-gray-500'>
          未設定のままにすると無期限で公開されます。
        </div>

        <Field label='ローテーション順' name='sortOrder' required>
          <InputNumber
            placeholder='並び順'
            min={0}
            className='w-[120px] bg-white'
            style={{ backgroundColor: '#fff' }}
          />
        </Field>

        <Field label='公開状態' name='status' className='w-full'>
          <RadioGroup options={statusOptions} />
        </Field>
      </Spin>
    </Form>
  );
};
